import React, { useCallback, useEffect, useState } from 'react'
import { useIntl } from 'react-intl'
import styled, { useTheme } from 'styled-components'
import defaultIcon from '../../../assets/icons/userPositionIcon.svg'
import { useAppProviderContext, useDropOffLocationContext, useMapContext } from '../../../core/application/contexts'
import { CreateDropoffLocationErrors } from '../../../core/application/formValidation/createDropoffFormValidator'
import { breakpoints } from '../../../core/application/theme/breakpoints'
import { MapProps, WoosmapMap, WoosmapMapEventListener, WoosmapMarker } from '../../../core/data/woosmap/woosmapSdk'
import { NewDropoffLocation } from '../../../core/domain/models/NewDropoff'
import { AddressResult, IGeocodingProvider } from '../../../core/domain/providers/IGeocodingProvider'
import AddressSearchInput from './AddressSearchInput'

export interface ISubmitDropoffLocationProps {
  location?: NewDropoffLocation
  updateLocation: (location: NewDropoffLocation) => void
  errors?: CreateDropoffLocationErrors
  shouldDisplayErrors: boolean
}

interface IMarkerPosition {
  latitude: number
  longitude: number
}

const SubmitDropoffLocation: React.FC<ISubmitDropoffLocationProps> = ({ location, updateLocation, errors }) => {
  const intl = useIntl()
  const theme = useTheme()
  const { geocodingProvider, dropOffTypeProvider } = useAppProviderContext()
  const { position } = useMapContext()
  const { woosmapMapReady } = useDropOffLocationContext()

  const [woosmapMap, setWoosmapMap] = useState<WoosmapMap | null>(null)

  // Where the map will be centered on launch
  // - on address if already selected
  // - on user location if available
  // - on tenant's default map coordinates if available

  /**
   * Update!
   * On launch, the map will be centered where the primary map is centered
   * cf: https://gitlab.com/veolia.com/vesa/digital-factory/urban-resourcer/drop-off-search-engine/-/issues/159
   *
   */
  const defaultMapLocation: () => IMarkerPosition | null = () => {
    if (location) {
      return {
        longitude: location.longitude,
        latitude: location.latitude
      }
    }
    /* if (geolocation?.coordinates) {
      return {
        longitude: geolocation.coordinates.longitude,
        latitude: geolocation.coordinates.latitude
      }
    }
    if (tenantConfiguration?.initialCoordinate) {
      return {
        longitude: tenantConfiguration.initialCoordinate.longitude,
        latitude: tenantConfiguration.initialCoordinate.latitude
      }
    } */
    return {
      latitude: position.lat,
      longitude: position.lng
    }
  }

  const [defaultLocation] = useState<IMarkerPosition | null>(defaultMapLocation())
  const [markerPosition, setMarkerPosition] = useState<IMarkerPosition>()
  const [address, setAddress] = useState<string>(location ? location.formattedAddress : '')
  const [searchedAddress, setSearchedAddress] = useState<string>('')
  const [marker, setMarker] = useState<WoosmapMarker | null>(null)
  const [editMapLocation, setEditMapLocation] = useState<boolean>(false)

  useEffect(() => {
    if (location && setAddress) {
      setAddress(location.formattedAddress)
    }
  }, [location, setAddress])

  useEffect(() => {
    if (editMapLocation) {
      setSearchedAddress('')
    } else {
    }
  }, [editMapLocation])

  useEffect(() => {
    if (!editMapLocation && marker && woosmapMap) {
      const { lat, lng } = marker.getPosition()
      woosmapMap.panTo({
        lat: lat(),
        lng: lng()
      })
    }
  }, [editMapLocation, marker, woosmapMap])

  useEffect(() => {
    const mapProps: Partial<MapProps> = {
      disableDefaultUI: true,
      disableTilt: true,
      zoom: 15,
      center: defaultLocation
        ? {
          lat: defaultLocation.latitude,
          lng: defaultLocation.longitude
        }
        : undefined
    }
    const mapContainer = document.getElementById('mini-map')
    if (mapContainer && woosmapMapReady) {
      const map = new window.woosmap.map.Map(mapContainer, mapProps)
      setWoosmapMap(map)
    }
  }, [defaultLocation, woosmapMapReady])

  //Create marker with state coordinatess
  useEffect(() => {
    let onDragListener: WoosmapMapEventListener
    let test: WoosmapMapEventListener
    const iconUrl = dropOffTypeProvider
      ? dropOffTypeProvider.generateIconUrl('default', theme.color.primary)
      : defaultIcon
    let newMarker: WoosmapMarker
    if (woosmapMap) {
      const { lat, lng } = woosmapMap.getCenter()
      newMarker = new window.woosmap.map.Marker({
        draggable: false,
        position: {
          lat: lat(),
          lng: lng()
        },
        icon: {
          url: iconUrl,
          scaledSize: {
            height: 42,
            width: 30
          }
        }
      })

      const onDragend = () => {
        const position = newMarker.getPosition()
        setMarkerPosition({
          latitude: position.lat(),
          longitude: position.lng()
        })
      }
      onDragListener = newMarker.addListener('dragend', onDragend)
      newMarker.setMap(woosmapMap)
      setMarker(newMarker)
    }

    return () => {
      if (newMarker) {
        newMarker.setMap(null)
      }
      onDragListener && onDragListener.remove()
      test && test.remove()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [woosmapMap, dropOffTypeProvider, setMarkerPosition])

  useEffect(() => {
    if (marker) {
      marker.setDraggable(editMapLocation)
    }
  }, [editMapLocation, marker])

  // find address when user change marker position by draging it
  useEffect(() => {
    const findAddress = async (provider: IGeocodingProvider, latitude: number, longitude: number) => {
      const addressResultList = await provider.getAddressFromCoordinates(latitude, longitude)
      if (addressResultList.length > 0) {
        const address = addressResultList[0]
        setAddress(address.formattedAddress)
        setSearchedAddress(address.formattedAddress)

        updateLocation &&
        updateLocation({
          formattedAddress: address.formattedAddress,
          line: address.line,
          city: address.city,
          country: address.country,
          zipCode: address.zipCode,
          latitude,
          longitude
        })
      }
    }
    if (editMapLocation && geocodingProvider && markerPosition && marker) {
      findAddress(geocodingProvider, markerPosition.latitude, markerPosition.longitude)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editMapLocation, geocodingProvider, markerPosition])

  const onAddressSelect = useCallback(
    (address: AddressResult) => {
      const position = marker?.getPosition()
      if (position?.lat() !== address.latitude && position?.lng() !== address.longitude) {
        marker?.setPosition({
          lat: address.latitude,
          lng: address.longitude
        })
        woosmapMap &&
        woosmapMap.panTo({
          lat: address.latitude,
          lng: address.longitude
        })

        updateLocation({
          formattedAddress: address.formattedAddress,
          line: address.line,
          zipCode: address.zipCode,
          city: address.city,
          country: address.country,
          latitude: address.latitude,
          longitude: address.longitude
        })
      }
    },
    [marker, updateLocation, woosmapMap]
  )

  const onInputChange = useCallback((str: string) => {
    setSearchedAddress(str)
  }, [])

  const searchPlaceholder = (() => {
    if (editMapLocation) {
      return address
    }
    return intl.formatMessage({ id: 'SubmitDropoff.location.address.placeholder' })
  })()

  return (
    <SubmitDropoffLocationContainer>
      <AddressSearchInput
        errors={errors}
        editMapLocation={editMapLocation}
        onSelect={onAddressSelect}
        value={searchedAddress}
        placeholder={searchPlaceholder}
        onChange={onInputChange}
      />
      <Address>{address}</Address>
      <MapSection edit={editMapLocation}>
        <MapWrapper id='mini-map' />
        <ButtonWrapper>
          <SwitchModeButton type='button' onClick={() => setEditMapLocation((value) => !value)}>
            {intl.formatMessage({ id: editMapLocation ? 'SubmitDropoff.location.set' : 'SubmitDropoff.location.edit' })}
          </SwitchModeButton>
        </ButtonWrapper>
      </MapSection>
    </SubmitDropoffLocationContainer>
  )
}

const SubmitDropoffLocationContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
`

const Address = styled.div`
  padding-bottom: 8px;
  margin-top: 12px;
  text-align: left;
  font-style: normal;
  font-weight: ${({ theme }) => theme.weight.normal};
  font-size: ${({ theme }) => theme.size.smaller};
  color: ${({ theme }) => theme.color.black};
`

interface MapSectionProps {
  edit: boolean
}

const MapSection = styled.div<MapSectionProps>`
  display: flex;
  flex-direction: column;
  position: relative;
  margin-bottom: 26px;
  border-radius: 8px;
  overflow: hidden;
  border-style: ${({ edit }) => (edit ? 'solid' : 'none')};
  border-width: ${({ edit }) => (edit ? '2px' : '0')};
  border-color: ${({ edit, theme }) => (edit ? theme.color.minimapBorder : 'none')};
`

const MapWrapper = styled.div`
  height: 250px;
  width: 100%;
`

const ButtonWrapper = styled.div`
  display: none;
  @media ${breakpoints.tablet} {
    display: block;
  }
  position: absolute;
  z-index: 2;
  bottom: 4px;
  left: 4px;
`

const SwitchModeButton = styled.button`
  border-radius: 4px;
  background-color: #f2f2f2;
  padding: 8px 16px;
  font-size: ${({ theme }) => theme.size.small};
  font-family: ${({ theme }) => theme.font.semi};
  font-weight: ${({ theme }) => theme.weight.bold};
  color: ${({ theme }) => theme.color.black};
  cursor: pointer;
`

export default SubmitDropoffLocation
